import React from 'react';
import {Button, Collapse, Empty, Tooltip} from 'antd';
import styled from 'styled-components';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheckCircle, faExclamationCircle, faChevronRight, faCircle} from '@fortawesome/pro-solid-svg-icons';
import {faExclamationCircle as faExclamationCircleRegular, faCircle as faCircleRegular, faCircleInfo} from '@fortawesome/pro-regular-svg-icons';
import styles from './styles.module.scss';
import {useRouter} from 'next/router';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react';
interface UrlItem {
  url: string;
  status: 'deploy' | 'failed' | 'undeployed';
}

interface SubGroup {
  group: string;
  label: string;
  totalDeployments: number;
  fixedDeployments: number;
  urls: UrlItem[];
}

interface GroupData {
  name: string;
  totalDeployments: number;
  fixedDeployments: number;
  subGroups: SubGroup[];
}

interface CardCollapseProps {
  group: GroupData;
  filter?: 'success' | 'error' | null;
  isZeroDeploymentGroup?: boolean;
  setVisible: any;
  defaultExpanded?: boolean;
}

const {Panel} = Collapse;

const CardCollapse: React.FC<CardCollapseProps> = observer(({group, filter, isZeroDeploymentGroup, setVisible, defaultExpanded}) => {
  const router = useRouter();
  const {ottoV2Store: {setActiveTab, setSelectedCategory, setDiagnosticIssue, activeTab, getOttoV2Project, setIsDeploying}} = useStore('');
  const getStatusDot = (status: string) => {
    const styling = {zIndex: 10, width: 10, height: 10, position: 'relative' as const, left: 2.5};

    switch (status) {
      case 'undeployed':
        return <FontAwesomeIcon icon={faCircleRegular} color='#7A7B7B' style={{...styling, backgroundColor: 'white', borderRadius: '50%'}} />;
      case 'deploy':
        return <FontAwesomeIcon icon={faCircleRegular} color='#1FAC47' style={{...styling, backgroundColor: 'white', borderRadius: '50%'}} />;
      default:
        return <FontAwesomeIcon icon={faCircle} color='#F44343' style={styling} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'undeployed':
        return '#7A7B7B';
      case 'deploy':
        return '#1FAC47';
      default:
        return '#F44343';
    }
  };

  const isCompleted = (fixed: number, total: number) => fixed >= total;

  // Filter URLs based on status
  const filterUrls = (urls: UrlItem[]) => {
    if (!filter) return urls;
    return urls.filter(url => {
      if (filter === 'success') return url.status === 'deploy';
      return url.status === 'failed' || url.status === 'undeployed';
    });
  };

  // Filter subgroups based on completion status and URLs
  const filterSubGroups = (subGroups: SubGroup[]) => {
    if (!filter) return subGroups;

    return subGroups.filter(subGroup => {
      const isSubGroupCompleted = isCompleted(subGroup.fixedDeployments, subGroup.totalDeployments);
      const filteredUrls = filterUrls(subGroup.urls);

      if (filter === 'success') {
        return isSubGroupCompleted && filteredUrls.length > 0;
      }
      return !isSubGroupCompleted && filteredUrls.length > 0;
    }).map(subGroup => ({
      ...subGroup,
      urls: filterUrls(subGroup.urls),
    }));
  };

  // Check if group should be shown based on filter
  const shouldShowGroup = () => {
    if (!filter) return true;
    const isGroupCompleted = isCompleted(group.fixedDeployments, group.totalDeployments);
    return filter === 'success' ? isGroupCompleted : !isGroupCompleted;
  };

  const filteredSubGroups = filterSubGroups(group.subGroups);

  if (!shouldShowGroup() || filteredSubGroups.length === 0) {
    return (
      <EmptyContainer>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No Deployments Found'
        />
      </EmptyContainer>
    );
  }


  const onIssueClick = async (e, subGroup) => {
    e.stopPropagation();
    const issue = getOttoV2Project?.issueTypesFrontendMappingV2?.find(group =>
      group.subGroups?.some(subgroup => subgroup.group === subGroup.group));
    const isOttoMainpage = router.pathname === '/seo-automation/tasks';
    if (isOttoMainpage) {
      if (activeTab === issue?.group) {
        setSelectedCategory(subGroup?.group);
      } else {
        setDiagnosticIssue(subGroup?.group);
        setIsDeploying(false);
        await router.push(`/seo-automation/tasks?uuid=${getOttoV2Project?.uuid}&category=${issue?.group}&subGroup=${subGroup.group}`, null, {shallow: true});
        setActiveTab(issue?.group);
      }
      setVisible(false);
    } else {
      setDiagnosticIssue(subGroup?.group);
      router.push(`/seo-automation/tasks?uuid=${getOttoV2Project?.uuid}&category=${issue?.group}&subGroup=${subGroup.group}`);
    }
  };

  return (
    <CollapseStyled $isZeroDeployment={isZeroDeploymentGroup} defaultActiveKey={defaultExpanded ? [group.name] : []}>
      <Panel
        header={
          <HeaderContainer>
            <LeftSection>
              <StatusIcon>
                {group.totalDeployments === 0 ? (
                  <FontAwesomeIcon icon={faCircleInfo} color='#A3A4A4' />
                ) : isCompleted(group.fixedDeployments, group.totalDeployments) ? (
                  <FontAwesomeIcon icon={faCheckCircle} color='#1FAC47' />
                ) : (
                  <FontAwesomeIcon icon={faExclamationCircle} color='#F44343' />
                )}
              </StatusIcon>
              <GroupTitle>{group.name}</GroupTitle>
            </LeftSection>
            <RightSection>
              <CountBadge style={{backgroundColor: group.totalDeployments === 0 ? '#f5f5f5' : '#fff', border: '1px solid #EEEEEE', color: group.totalDeployments === 0 ? '#A3A4A4' : undefined}}>
                <span>{group.fixedDeployments}/{group.totalDeployments}</span>
              </CountBadge>
              <CollapseArrow className='collapse-arrow'>
                <FontAwesomeIcon icon={faChevronRight} />
              </CollapseArrow>
            </RightSection>
          </HeaderContainer>
        }
        key={group.name}
      >
        <SubGroupContainer style={{paddingBottom: 10}}>
          {filteredSubGroups.map(subGroup => {
            const completed = isCompleted(subGroup.fixedDeployments, subGroup.totalDeployments);
            const hasUrls = subGroup.totalDeployments !== 0 && subGroup.urls && subGroup.urls.length > 0;
            const isZeroDeployment = subGroup.totalDeployments === 0;
            // New logic for banner color
            const hasFailed = hasUrls && subGroup.urls.some(url => url.status === 'failed');
            const allUndeployed = hasUrls && subGroup.urls.every(url => url.status === 'undeployed');
            const allDeployed = hasUrls && subGroup.urls.every(url => url.status === 'deploy');
            const isSuccess = subGroup.totalDeployments > 0 && subGroup.fixedDeployments === subGroup.totalDeployments;
            let bannerBg = undefined;
            let bannerColor = undefined;
            if (hasFailed) {
              // Use existing failed logic (red)
              bannerBg = 'rgba(244, 67, 67, 0.05)';
              bannerColor = '#F44343';
            } else if (allUndeployed) {
              bannerBg = '#f5f5f5';
              bannerColor = '#7A7B7B';
            } else if (allDeployed) {
              // Use existing success logic (green)
              bannerBg = 'rgba(31, 172, 71, 0.1)';
              bannerColor = '#1FAC47';
            } else if (isZeroDeployment) {
              bannerBg = '#f5f5f5';
              bannerColor = '#7A7B7B';
            }
            if (isSuccess) {
              bannerBg = 'rgba(31, 172, 71, 0.1)';
              bannerColor = '#1FAC47';
            }
            return (
              <SubGroupCollapse
                key={subGroup.group}
                $isCompleted={completed}
                $isZeroDeployment={isZeroDeployment}
                style={bannerBg ? {background: bannerBg} : {}}
              >
                <Panel
                  header={
                    <HeaderContainer>
                      <LeftSection>
                        <StatusIcon>
                          {isSuccess ? (
                            <FontAwesomeIcon icon={faCheckCircle} color={bannerColor} />
                          ) : hasFailed ? (
                            <FontAwesomeIcon icon={faExclamationCircleRegular} color={bannerColor} />
                          ) : allUndeployed ? (
                            <FontAwesomeIcon icon={faCircleInfo} color={bannerColor} />
                          ) : isZeroDeployment ? (
                            <FontAwesomeIcon icon={faCircleInfo} color={bannerColor} />
                          ) : (
                            <FontAwesomeIcon icon={faExclamationCircleRegular} color='#F44343' />
                          )}
                        </StatusIcon>
                        <SubGroupTitle style={{color: bannerColor}}>{subGroup.label}</SubGroupTitle>
                      </LeftSection>
                      <RightSection>
                        <Button className={styles.viewButton} onClick={e => onIssueClick(e, subGroup)}>
                          View Issue
                        </Button>
                        <CountBadge style={{backgroundColor: bannerBg, color: bannerColor}}>
                          <span>{subGroup.fixedDeployments}/{subGroup.totalDeployments}</span>
                        </CountBadge>
                        <CollapseArrow className='subgroup-collapse-arrow' style={{visibility: hasUrls ? 'visible' : 'hidden', transform: 'rotate(90deg)'}}>
                          <FontAwesomeIcon icon={faChevronRight} />
                        </CollapseArrow>
                      </RightSection>
                    </HeaderContainer>
                  }
                  key={subGroup.group}
                  collapsible={!hasUrls ? 'disabled' : undefined}
                >
                  <UrlsSection>
                    <UrlList>
                      {[...subGroup.urls]
                        .sort((a, b) => {
                          if (a.status === 'undeployed' && b.status !== 'undeployed') return 1;
                          if (a.status !== 'undeployed' && b.status === 'undeployed') return -1;
                          return 0;
                        })
                        .map((url, index) => (
                          <ListItem key={index}>
                            <VerticalLine style={{height: '100%', backgroundColor: isSuccess ? '#1FAC47' : bannerColor, width: 1.5}} />
                            {getStatusDot(url.status)}
                            <Tooltip title={url.url} overlayInnerStyle={{width: 300}}>
                              <UrlText
                                style={{
                                  color: getStatusColor(url.status),
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '100%',
                                }}
                              >
                                {url.url}
                              </UrlText>
                            </Tooltip>
                          </ListItem>
                        ))}
                    </UrlList>
                  </UrlsSection>
                </Panel>
              </SubGroupCollapse>
            );
          })}
        </SubGroupContainer>
      </Panel>
    </CollapseStyled>
  );
});

const CollapseStyled = styled(Collapse)<{$isZeroDeployment?: boolean}>`
  background: ${props => props.$isZeroDeployment ? '#f5f5f5' : '#FFFFFF'};
  border-radius: 8px;
  margin-top: 12px;
  border: none;
  overflow: hidden;


  .ant-collapse-item {
    border: none !important;
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
    align-items: center !important;
  }

  .ant-collapse-content {
    border: none !important;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-expand-icon {
    display: none !important;
  }

  .collapse-arrow {
    transform: rotate(0deg);
    transition: transform 0.2s ease;
  }

  .ant-collapse-item-active {
    .collapse-arrow {
      transform: rotate(90deg);
    }
  }
`;

const SubGroupCollapse = styled(Collapse)<{$isCompleted: boolean; $isZeroDeployment?: boolean}>`
  background: ${props => props.$isZeroDeployment ? '#f5f5f5' : props.$isCompleted ? 'rgba(31, 172, 71, 0.1)' : 'rgba(244, 67, 67, 0.05)'};
  border-radius: 8px;
  margin-bottom: 8px;
  width: 100%;
  margin-left: 0;
  margin-right: 0;
  border: none;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  .ant-collapse-item {
    background: transparent;
    border: none !important;
    padding: 0 !important;
  }

  .ant-collapse-header {
    padding: 10px 12px !important;
    // align-items: center !important;
    background: transparent;
  }

  .ant-collapse-content {
    background: transparent;
    border: none !important;
  }

  .ant-collapse-content-box {
    padding: 0 12px 10px !important;
  }

  .ant-collapse-expand-icon {
    display: none !important;
  }

  .collapse-arrow {
    transform: rotate(0deg);
    transition: transform 0.2s ease;
  }

  .ant-collapse-item-active {
    > .ant-collapse-header {
      .subgroup-collapse-arrow {
        transform: rotate(-90deg) !important;
      }
    }
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CollapseArrow = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666666;
`;

const GroupTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  margin: 0;
`;

const SubGroupTitle = styled.h4`
  font-size: 13px;
  font-weight: 500;
  color: #333333;
  margin: 0;
`;

const UrlsSection = styled.div`
  position: relative;
  display: flex;
  // padding-left: 20px;
  // margin-left: 8px;
`;

const VerticalLine = styled.div`
    position: absolute;
    left: 7px;
    top: -13px;
    bottom: 0;
    width: 1px;
    background: #E6E6E6;
`;

const UrlList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const ListItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  width: 100%;
`;

const StatusIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

const UrlText = styled.span`
  font-size: 12px;
  color: #333333;
  word-break: break-all;
`;

const CountBadge = styled.div`
  background: #E6F4F1;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  min-width: 24px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 0 16px;
`;

const EmptyContainer = styled.div`
  padding: 24px;
  background: #FFFFFF;
  border-radius: 8px;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  align-items: center;

  .ant-empty {
    .ant-empty-description {
      color: #666666;
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

export default CardCollapse;
